import { Sprout, Package, Snowflake, Ship, Warehouse, Truck } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { Eyebrow, Reveal } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

const ICONS = { Sprout, Package, Snowflake, Ship, Warehouse, Truck };

export default function ProcessFlow() {
  const { c } = useLang();
  const pr = c.process;

  return (
    <section id="model" data-testid="model-section" className="relative py-28 sm:py-40 bg-[#DCD6C1] border-y border-forest/10 overflow-hidden">
      <LeafDecor layers={[
        { img: "corner", className: "bottom-0 right-0 w-[460px] sm:w-[580px] -mb-24 -mr-24 rotate-[200deg]", opacity: 0.34 },
        { img: "bunch", className: "top-0 left-0 w-[320px] -mt-14 -ml-12 rotate-12", opacity: 0.3 },
        { img: "side", className: "bottom-1/4 left-1/2 h-[300px] rotate-[-14deg] hidden xl:block", opacity: 0.14 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <Eyebrow testid="model-eyebrow">{pr.eyebrow}</Eyebrow>
        <div className="mt-6 grid lg:grid-cols-12 gap-6 items-end">
          <h2 className="lg:col-span-7 font-serif font-light leading-none tracking-[-0.02em] text-forest text-5xl sm:text-6xl lg:text-7xl">
            {pr.heading.pre}<span className="text-gold italic">{pr.heading.em}</span>
          </h2>
          <p className="lg:col-span-5 font-sans text-sm leading-relaxed text-forest/60">{pr.subcopy}</p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pr.steps.map((step, i) => {
            const Icon = ICONS[step.icon];
            return (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div data-testid={`process-step-${i + 1}`} className="group relative h-full border border-black/15 bg-leaf p-7 hover:shadow-2xl transition-all duration-500 shadow-[0_16px_40px_-28px_rgba(3,22,12,0.5)]">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-serif text-5xl font-bold text-black/70">{`0${i + 1}`}</span>
                    <span className="grid place-items-center h-12 w-12 border border-black/40 rounded-full group-hover:bg-black transition-colors duration-500">
                      <Icon size={20} strokeWidth={2.2} className="text-black group-hover:text-leaf transition-colors duration-500" />
                    </span>
                  </div>
                  <div className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1.5">{step.place}</div>
                  <h3 className="font-serif text-2xl font-bold text-black mb-2">{step.title}</h3>
                  <p className="font-sans text-sm font-semibold leading-relaxed text-black/80">{step.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-forest/10 pt-6 font-sans text-xs uppercase tracking-[0.16em] text-forest/60">
            <span className="text-gold">{pr.targetLabel}</span>
            {pr.targets.map((t) => (<span key={t}>{t}</span>))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
